/*
 * StatsCard.jsx
 * -------------
 * Displays a single lifetime statistic on the Dashboard.
 * Shows a large number, a label, an icon, and an optional
 * trend indicator comparing to last week.
 * Animates the number counting up on mount for delight.
 * Used in a row of four cards at the top of Dashboard.
 */

import React, { useEffect, useState, useRef } from "react";

// ============================================
// COUNT UP ANIMATION
// ============================================
function useCountUp(target, duration = 1200) {
  const [count, setCount] = useState(0);
  const frameRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (target === 0) { setCount(0); return; }

    function animate(timestamp) {
      if (!startRef.current) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    }

    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);

  return count;
}

// ============================================
// TREND INDICATOR
// ============================================
function TrendIndicator({ trend }) {
  if (trend === null || trend === undefined) return null;

  const isPositive = trend > 0;
  const isNeutral = trend === 0;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "3px",
      fontFamily: "var(--font-body)",
      fontSize: "12px",
      fontWeight: 600,
      color: isNeutral
        ? "var(--text-muted)"
        : isPositive
          ? "var(--accent-green)"
          : "var(--status-overdue)"
    }}>
      {!isNeutral && (
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          style={{
            transform: isPositive ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform var(--transition-fast)"
          }}
        >
          <path
            d="M5 2L9 7H1L5 2Z"
            fill="currentColor"
          />
        </svg>
      )}
      {isNeutral ? "Same as last week" : `${Math.abs(trend)} vs last week`}
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function StatsCard({
  icon,
  label,
  value,
  suffix = "",
  color = "var(--accent-green)",
  trend = null,
  animateCount = true
}) {
  const displayValue = animateCount
    ? useCountUp(typeof value === "number" ? value : 0)
    : value;

  return (
    <div style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-md)",
      padding: "var(--space-5)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-3)",
      boxShadow: "var(--shadow-sm)",
      position: "relative",
      overflow: "hidden",
      flex: 1,
      minWidth: 0
    }}>

      {/* Background accent circle */}
      <div style={{
        position: "absolute",
        top: "-20px",
        right: "-20px",
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        background: color,
        opacity: 0.08
      }} />

      {/* Icon + label row */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <span style={{
          fontFamily: "var(--font-body)",
          fontSize: "13px",
          fontWeight: 500,
          color: "var(--text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.05em"
        }}>
          {label}
        </span>
        <span style={{ fontSize: "20px" }}>{icon}</span>
      </div>

      {/* Value */}
      <div style={{
        fontFamily: "var(--font-display)",
        fontWeight: 900,
        fontSize: "clamp(28px, 4vw, 40px)",
        color,
        lineHeight: 1,
        letterSpacing: "-1px"
      }}>
        {typeof value === "number" ? displayValue : value}
        {suffix && (
          <span style={{
            fontSize: "16px",
            fontWeight: 600,
            color: "var(--text-muted)",
            marginLeft: "4px",
            letterSpacing: 0
          }}>
            {suffix}
          </span>
        )}
      </div>

      {/* Trend */}
      <TrendIndicator trend={trend} />
    </div>
  );
}